/**
 * **L'accessibilité, depuis l'Atelier** — SPEC-UI §7.3 : « toggle dans les paramètres (⚙,
 * accessible partout y compris dans l'Atelier) », « modifiable entre deux étapes, jamais
 * pendant une étape en cours ».
 *
 * Un dialogue maison (jamais `window.confirm` — CLAUDE.md, §7.1) posé PAR-DESSUS l'Atelier :
 * on ne quitte pas la production pour régler sa tolérance. Il porte les trois réglages qui
 * comptent pendant qu'on joue — la tolérance, le son, l'état du mouvement réduit — et renvoie
 * vers la chambre pour le reste (le calage audio a son mini-test dans les Réglages).
 *
 * ## La tolérance, et ce qu'elle fait à une production en cours
 *
 * - **Pendant une étape** (`phase === "jeu"`), la case est verrouillée : un pattern en cours
 *   de jeu ne change pas de fenêtres sous les doigts.
 * - **Entre deux étapes**, côté compte, le réglage part au serveur (`flags.accessibility_
 *   tolerance`, §5.9) et l'Atelier **redemande ses patterns** : même tour ⇒ mêmes tirages, seules
 *   les fenêtres et chronos changent. Le serveur note TOUTE la production avec le réglage en
 *   vigueur à la publication (§6.4) — c'est dit ici, noir sur blanc, pour que personne ne croie
 *   avoir joué une étape « sans » et une autre « avec ».
 *
 * ⟦C57 · lot 8 · A-174⟧ **Moins de texte** : chaque réglage garde son nom et UNE ligne courte (« +30 % de
 * temps, plafond 85 »). Les phrases d'avant (le verrou pendant une étape, ce que fait le serveur, ce que
 * couvre le son, le mouvement réduit, le calage audio) sont derrière « Les dessous » du dialogue,
 * mot pour mot.
 */

import { useEffect, useRef } from "react";

import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { synchroniserTolerance } from "../../game/client";
import { useUIStore } from "../../stores/uiStore";

export function AccessibiliteAtelier({
  ouvert,
  enPleinJeu,
  surServeur,
  onFermer,
  onToleranceChangee,
}: {
  ouvert: boolean;
  /** Une étape est en cours : la tolérance ne se change pas au milieu d'un geste (§7.3). */
  enPleinJeu: boolean;
  surServeur: boolean;
  onFermer(): void;
  /** Appelé APRÈS que le serveur a pris le réglage — l'Atelier redemande ses patterns. */
  onToleranceChangee(active: boolean): void;
}) {
  const settings = useUIStore((s) => s.settings);
  const setSetting = useUIStore((s) => s.setSetting);
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const premier = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!ouvert) return;
    premier.current?.focus();
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onFermer();
      }
    };
    document.addEventListener("keydown", auClavier);
    return () => document.removeEventListener("keydown", auClavier);
  }, [ouvert, onFermer]);

  if (!ouvert) return null;

  async function changerTolerance(active: boolean) {
    setSetting("tolerance", active);
    // Côté compte : le réglage vit dans l'ÉTAT ; on attend que le serveur l'ait pris avant de
    // redemander les patterns, sinon on recevrait ceux d'avant.
    await synchroniserTolerance(active);
    onToleranceChangee(active);
  }

  return (
    <div className="overlay" role="presentation">
      <div
        className="dialogue accessibilite-atelier"
        role="dialog"
        aria-modal="true"
        aria-labelledby="accessibilite-titre"
      >
        <h2 id="accessibilite-titre" className="dialogue-titre">
          ⚙ Accessibilité
        </h2>

        <label className="reglage">
          <input
            ref={premier}
            type="checkbox"
            checked={settings.tolerance}
            disabled={enPleinJeu}
            onChange={(e) => void changerTolerance(e.target.checked)}
          />
          <span>
            <strong>Tolérance de timing</strong>
            <span className="reglage-aide">
              {enPleinJeu ? "+30 % de temps, plafond 85 · verrouillée en jeu." : "+30 % de temps, plafond 85."}
            </span>
          </span>
        </label>

        <label className="reglage">
          <input
            type="checkbox"
            checked={!settings.mute}
            onChange={(e) => setSetting("mute", !e.target.checked)}
          />
          <span>
            <strong>Le son</strong>
            <span className="reglage-aide">Aucun effet sur le score.</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={settings.volume}
              disabled={settings.mute}
              aria-label="Volume"
              onChange={(e) => setSetting("volume", Number(e.target.value))}
            />
          </span>
        </label>

        <p className="ligne-info discret">
          Mouvement réduit : {reducedMotion ? "mode posé." : "non détecté."}
        </p>

        <div className="dialogue-actions">
          <LesDessous titre="Accessibilité de l'Atelier">
            <DessousBloc titre="Tolérance de timing">
              <p className="dessous-texte">
                Fenêtres et chronos allongés de 30 %. En échange, chaque mini-jeu est plafonné à 85 :
                une aide, pas un raccourci.
              </p>
              <p className="dessous-texte">
                Verrouillée pendant une étape : finis ton geste, elle se rouvre à l'écran de score.
              </p>
              {surServeur ? (
                <p className="dessous-texte">
                  Le serveur note toute la production avec le réglage en vigueur au moment de publier :
                  changer ici change pour les étapes déjà jouées aussi.
                </p>
              ) : null}
            </DessousBloc>
            <DessousBloc titre="Le son">
              <p className="dessous-texte">
                Les frappes, les chronos, la musique du Montage. Couper le son ne change aucun score.
              </p>
            </DessousBloc>
            <DessousBloc titre="Mouvement réduit">
              <p className="dessous-texte">
                {reducedMotion
                  ? "Détecté : les mini-jeux sont en mode posé (plafonné à 85 aussi)."
                  : "Non détecté. Il se lit dans ton système, il ne se règle pas ici."}
              </p>
              <p className="dessous-texte">
                Le calage audio (mini-test à 90 BPM) se règle dans les Réglages, depuis la chambre.
              </p>
            </DessousBloc>
          </LesDessous>
          <button type="button" className="bouton primaire" onClick={onFermer}>
            Revenir à l'Atelier
          </button>
        </div>
      </div>
    </div>
  );
}
